"use client";

import { ChevronDown, MessageCircle, ArrowRight } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  { id: 1, category: "Platform", question: "What is Ren Power Group?", answer: "Ren Power Group is Nigeria's premier solar energy marketplace that connects homeowners, businesses, manufacturers, distributors, and recyclers in one intelligent platform. We make it easy to find trusted solar partners, request quotes, and access sustainable energy solutions across Nigeria." },
  { id: 2, category: "Platform", question: "Who can use the Ren Power Group platform?", answer: "The platform is designed for four main user types: Homeowners seeking solar solutions, Manufacturers producing solar equipment, Distributors selling solar products, and Recyclers handling end-of-life components. Each user type has tailored features for their specific needs." },
  { id: 3, category: "Platform", question: "Is there a fee to use Ren Power Group?", answer: "Browsing products, requesting quotes, and connecting with providers is completely free for homeowners and businesses. Manufacturers, distributors, and recyclers may have subscription plans for enhanced visibility and features. There are no hidden fees for end users." },
  { id: 4, category: "Quotes", question: "How does the quote system work?", answer: "Browse our marketplace to find solar products. When you find what you need, request a quote by providing your requirements. Manufacturers and distributors respond with pricing and availability. Compare quotes and choose the best option for your budget." },
  { id: 5, category: "Quotes", question: "How long does it take to get quotes?", answer: "Most quotes are received within 24–48 hours. You'll receive notifications when providers respond. All your quotes are accessible in your dashboard where you can compare them side by side." },
  { id: 6, category: "Products", question: "What types of solar products are available?", answer: "Our marketplace features solar panels, inverters, batteries, charge controllers, solar kits, installation services, maintenance packages, and recycling services. Filter by category, location, and budget to find exactly what you need." },
  { id: 7, category: "Products", question: "How much does solar installation cost in Nigeria?", answer: "Solar installation costs vary by energy needs, location, and system size. A typical home installation ranges from ₦500,000 to ₦5,000,000. Request quotes from multiple providers on our platform to compare and find the best deal for your budget." },
  { id: 8, category: "Trust", question: "Are the solar providers on your platform verified?", answer: "Yes. All manufacturers, distributors, and recyclers undergo a verification process. We ensure they meet quality standards and have legitimate business operations. View provider ratings, reviews, and credentials before requesting quotes." },
  { id: 9, category: "Trust", question: "Do you offer installation services?", answer: "Ren Power Group connects you with verified distributors and manufacturers who provide installation services. When requesting a quote, specify that you need installation. Our partners offer professional installation across Lagos, Abuja, Port Harcourt, and other major cities." },
  { id: 10, category: "Recycling", question: "What is the recycling program?", answer: "Our recycling program connects you with certified recyclers who responsibly dispose of old or damaged solar equipment. This ensures solar components are recycled rather than ending up in landfills. Services cover panels, batteries, and inverters." },
];

const categories = ["All", "Platform", "Quotes", "Products", "Trust", "Recycling"];

export default function FAQs() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All" ? faqData : faqData.filter(f => f.category === activeCategory);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero */}
      <section className="bg-[#002301] pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="faq-dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#faq-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <p className="text-[#17BC2E] text-sm font-semibold uppercase tracking-widest mb-3">Help Center</p>
          <h1 className="text-white text-4xl sm:text-5xl font-bold leading-tight mb-4">
            Frequently Asked <span className="text-[#FFB600]">Questions</span>
          </h1>
          <p className="text-white/60 text-base max-w-xl mx-auto leading-relaxed">
            Find answers about our solar marketplace, quote system, and how to connect with verified providers across Nigeria.
          </p>
        </motion.div>
      </section>

      {/* Category filter */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex gap-2 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === cat
                  ? "bg-[#002301] text-white"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* FAQ Accordion — 2 column grid */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((faq, i) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <span className="text-[10px] font-semibold text-[#17BC2E] uppercase tracking-widest block mb-1">{faq.category}</span>
                    <span className="text-sm font-semibold text-gray-800 leading-snug">{faq.question}</span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform duration-300 ${openId === faq.id ? "rotate-180 text-[#17BC2E]" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openId === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Still have questions CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-14 bg-[#002301] rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#17BC2E]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-[#17BC2E]" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">Still have questions?</p>
                <p className="text-white/60 text-sm">Our support team is ready to help you out.</p>
              </div>
            </div>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-[#FFB600] text-black font-semibold px-6 py-3 rounded-xl hover:bg-[#FFD700] transition-colors whitespace-nowrap"
            >
              Contact Support <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
